import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Spin } from "antd";
import {
  readCookie,
  consumeTokenFromHash,
  fetchAccessContext,
  persistAccessContext,
  SSO_COOKIE_SENTINEL,
  GINJA_COOKIE_NAME,
} from "../Utils/api/ginjaAuth";
import { getStoredAccessContext } from "../Utils/access/permissionHelpers";
import { setAccessContext } from "../Store/accessSlice";

const PUBLIC_PATHS = ["/login", "/terms&conditions"];

/**
 * Non-blocking SSO bootstrap.
 *
 *  - Always seeds `auth-token` in localStorage (real token if available,
 *    otherwise a sentinel) so PrivateRoute lets the user in.
 *  - Fires `/me/access` in the background to populate user info.
 *  - A global axios interceptor redirects to parent on 401 from any API,
 *    which is the only reliable signal that the user isn't authed.
 */
const AuthBootstrap = ({ children }) => {
  const [ready, setReady] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const existing = getStoredAccessContext();
    if (existing) {
      dispatch(setAccessContext(existing));
    }

    const bootstrap = async () => {
      const path = window.location.pathname || "";
      const isPublic = PUBLIC_PATHS.some((p) => path.startsWith(p));

      // 1. Capture any token the parent chose to hand off (hash or readable cookie).
      const hashToken = consumeTokenFromHash();
      const cookieToken = readCookie(GINJA_COOKIE_NAME);
      const readableToken = hashToken || cookieToken;
      const existingToken = localStorage.getItem("auth-token");

      // 2. Seed localStorage so PrivateRoute lets the user in while we load context.
      //    Actual auth is done by the HttpOnly cookie + withCredentials on each request.
      if (readableToken) {
        localStorage.setItem("auth-token", readableToken);
      } else if (!existingToken && !isPublic) {
        localStorage.setItem("auth-token", SSO_COOKIE_SENTINEL);
      }

      // 3. Render immediately — don't block on /me/access.
      setReady(true);

      // 4. Fetch context in the background. Any failure is non-fatal; the 401
      //    interceptor above is what actually gates access.
      try {
        const { context, token } = await fetchAccessContext(readableToken);
        if (context) {
          const effectiveToken = token || readableToken || existingToken;
          persistAccessContext(effectiveToken, context);
          dispatch(setAccessContext(context));
        }
      } catch (err) {
        // Silently ignore — CORS/network errors shouldn't block the UI.
        console.warn("[AuthBootstrap] /me/access failed (non-fatal):", err?.message);
      }
    };

    bootstrap();
  }, []);

  if (!ready) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#f6ffed",
        }}
      >
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  return children;
};

export default AuthBootstrap;
